import { useState } from 'react';
import {
  FlaskConical,
  KeyRound,
  Rocket,
  Gauge,
  History,
  CheckCircle2,
  XCircle,
  Clock,
  Timer,
} from 'lucide-react';
import {
  useCurrentUser,
  useIssuanceTestConfig,
  useSaveIssuanceTestConfig,
  useIssuanceTestTemplates,
  useIssuanceTestRuns,
  useStartIssuanceTestRun,
  useIssuanceTestRunResult,
} from '../hooks/useApi';
import { DataTable } from '../components/shared/DataTable';
import { MetricCard } from '../components/shared/MetricCard';
import { StatusBadge } from '../components/shared/StatusBadge';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { ErrorAlert } from '../components/shared/ErrorAlert';
import type { IssuanceTestRequestStatus, IssuanceTestRunDetail } from '../types/api';

const MAX_COUNT = 500;

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('pl-PL');
}

export function IssuanceTestPage() {
  const currentUser = useCurrentUser();
  const isAdmin = currentUser.data?.isAdmin ?? false;

  const config = useIssuanceTestConfig();
  const saveConfig = useSaveIssuanceTestConfig();
  const configured = config.data?.configured ?? false;

  const templates = useIssuanceTestTemplates(configured);
  const runs = useIssuanceTestRuns();
  const startRun = useStartIssuanceTestRun();

  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const runResult = useIssuanceTestRunResult(activeRunId);

  // Config form state
  const [editingConfig, setEditingConfig] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [domainInput, setDomainInput] = useState('');
  const [configError, setConfigError] = useState<string | null>(null);

  // New test form state
  const [templateId, setTemplateId] = useState('');
  const [count, setCount] = useState(20);
  const [grade, setGrade] = useState('A');
  const [startError, setStartError] = useState<string | null>(null);

  const selectedTemplate = templates.data?.find((t) => t.id === templateId);

  const showConfigForm = editingConfig || !configured;

  const handleSaveConfig = async () => {
    setConfigError(null);
    try {
      await saveConfig.mutateAsync({
        apiKey: apiKeyInput.trim() || undefined,
        recipientDomain: domainInput.trim() || undefined,
      });
      setApiKeyInput('');
      setEditingConfig(false);
    } catch (err: any) {
      setConfigError(err.message);
    }
  };

  const handleStartEditing = () => {
    setDomainInput(config.data?.recipientDomain ?? '');
    setApiKeyInput('');
    setEditingConfig(true);
  };

  const handleStartRun = async () => {
    setStartError(null);
    if (!templateId) return;
    try {
      const summary = await startRun.mutateAsync({
        templateId,
        count,
        grade: selectedTemplate?.requiresGrade ? grade : undefined,
      });
      setActiveRunId(summary.runId);
    } catch (err: any) {
      setStartError(err.message);
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Brak dostepu</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
          <FlaskConical className="h-6 w-6 text-brand-600 dark:text-brand-400" />
          Test wystawiania
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Testowanie poprawności i tempa wystawiania poświadczeń przez publiczne API Credentium
        </p>
      </div>

      {/* --- Configuration --- */}
      <div className="card">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
          <KeyRound className="h-4 w-4" />
          Konfiguracja
        </h2>

        {config.isLoading ? (
          <LoadingSpinner size="sm" />
        ) : config.error ? (
          <ErrorAlert message={config.error.message} onRetry={() => config.refetch()} />
        ) : showConfigForm ? (
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                Klucz API organizacji {configured && '(pozostaw puste, aby nie zmieniać)'}
              </label>
              <input
                type="password"
                value={apiKeyInput}
                onChange={(e) => setApiKeyInput(e.target.value)}
                placeholder="cred_..."
                autoComplete="off"
                className="input-field w-full font-mono"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Domena odbiorców</label>
              <input
                type="text"
                value={domainInput}
                onChange={(e) => setDomainInput(e.target.value)}
                placeholder="demo-org.click"
                className="input-field w-full"
              />
              <p className="text-xs text-gray-400 mt-1">
                Poświadczenia trafią na losowe, nieistniejące adresy w tej domenie (maile się odbiją — mierzymy
                wystawienie, nie dostarczenie).
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleSaveConfig}
                disabled={saveConfig.isPending || (!configured && !apiKeyInput.trim())}
                className="btn-primary disabled:opacity-50"
              >
                {saveConfig.isPending ? 'Zapisywanie...' : 'Zapisz'}
              </button>
              {configured && (
                <button onClick={() => setEditingConfig(false)} className="btn-secondary">
                  Anuluj
                </button>
              )}
            </div>
            {configError && <p className="text-sm text-red-500">{configError}</p>}
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <StatusBadge label="Skonfigurowano" variant="success" />
                <span className="font-mono text-gray-500">{config.data?.apiKeyMasked}</span>
              </div>
              <p className="text-xs text-gray-500">
                Domena odbiorców: <span className="font-medium">{config.data?.recipientDomain}</span>
              </p>
              {config.data?.updatedBy && (
                <p className="text-xs text-gray-400">
                  Zaktualizowano przez {config.data.updatedBy}
                  {config.data.updatedAt && ` · ${formatDateTime(config.data.updatedAt)}`}
                </p>
              )}
            </div>
            <button onClick={handleStartEditing} className="btn-secondary text-sm">
              Edytuj
            </button>
          </div>
        )}
      </div>

      {/* --- New test --- */}
      {configured && (
        <div className="card">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
            <Rocket className="h-4 w-4" />
            Nowy test
          </h2>

          {templates.isLoading ? (
            <LoadingSpinner size="sm" />
          ) : templates.error ? (
            <ErrorAlert message={templates.error.message} onRetry={() => templates.refetch()} />
          ) : (
            <div className="flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-[220px]">
                <label className="block text-xs text-gray-500 mb-1">Szablon</label>
                <select
                  value={templateId}
                  onChange={(e) => setTemplateId(e.target.value)}
                  className="input-field w-full"
                >
                  <option value="">— wybierz szablon —</option>
                  {templates.data?.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.title}
                    </option>
                  ))}
                </select>
              </div>
              <div className="w-32">
                <label className="block text-xs text-gray-500 mb-1">Liczba</label>
                <input
                  type="number"
                  min={1}
                  max={MAX_COUNT}
                  value={count}
                  onChange={(e) => setCount(Math.min(Math.max(Number(e.target.value) || 1, 1), MAX_COUNT))}
                  className="input-field w-full"
                />
              </div>
              {selectedTemplate?.requiresGrade && (
                <div className="w-28">
                  <label className="block text-xs text-gray-500 mb-1">Ocena</label>
                  <input
                    type="text"
                    value={grade}
                    onChange={(e) => setGrade(e.target.value)}
                    className="input-field w-full"
                  />
                </div>
              )}
              <button
                onClick={handleStartRun}
                disabled={!templateId || startRun.isPending}
                className="btn-primary disabled:opacity-50"
              >
                {startRun.isPending ? 'Zlecanie...' : 'Rozpocznij test'}
              </button>
            </div>
          )}
          {selectedTemplate?.requiresGrade && (
            <p className="text-xs text-gray-400 mt-2">Wybrany szablon wymaga oceny.</p>
          )}
          {startError && <p className="text-sm text-red-500 mt-2">{startError}</p>}
          {templates.data && templates.data.length === 0 && (
            <p className="text-sm text-gray-500 mt-2">
              Brak szablonów dostępnych dla skonfigurowanego klucza API.
            </p>
          )}
        </div>
      )}

      {/* --- Current run results --- */}
      {activeRunId && (
        <div className="card">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
            <Gauge className="h-4 w-4" />
            Wynik testu
          </h2>
          {runResult.isLoading ? (
            <LoadingSpinner size="sm" label="Pobieranie wyników..." />
          ) : runResult.error ? (
            <ErrorAlert message={runResult.error.message} onRetry={() => runResult.refetch()} />
          ) : runResult.data ? (
            <RunResultView detail={runResult.data} />
          ) : null}
        </div>
      )}

      {/* --- History --- */}
      <div className="card">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
          <History className="h-4 w-4" />
          Ostatnie testy
        </h2>
        {runs.isLoading ? (
          <LoadingSpinner size="sm" />
        ) : runs.error ? (
          <ErrorAlert message={runs.error.message} onRetry={() => runs.refetch()} />
        ) : runs.data && runs.data.length > 0 ? (
          <div className="divide-y divide-gray-100 dark:divide-gray-800/50">
            {runs.data.map((run) => (
              <button
                key={run.runId}
                onClick={() => setActiveRunId(run.runId)}
                className={`w-full flex items-center justify-between py-2.5 px-2 -mx-2 rounded-lg text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/30 ${
                  run.runId === activeRunId ? 'bg-brand-50 dark:bg-brand-600/10' : ''
                }`}
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {run.templateTitle}
                  </p>
                  <p className="text-xs text-gray-500">
                    {run.requestedCount} szt. · {run.recipientDomain} · {formatDateTime(run.startedAt)}
                    {run.startedBy && ` · ${run.startedBy}`}
                  </p>
                </div>
                {run.submitFailedCount > 0 && (
                  <StatusBadge label={`${run.submitFailedCount} odrzuconych`} variant="danger" />
                )}
              </button>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">Brak testów. Uruchom pierwszy test powyżej.</p>
        )}
      </div>
    </div>
  );
}

function RunResultView({ detail }: { detail: IssuanceTestRunDetail }) {
  const avg = detail.avgIssuanceSeconds;
  const throughput = detail.throughputPerMinute;

  const columns = [
    {
      key: 'issueRequestId',
      header: 'Request ID',
      render: (row: IssuanceTestRequestStatus) => (
        <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
          {row.issueRequestId.length > 8 ? `${row.issueRequestId.substring(0, 8)}...` : row.issueRequestId}
        </span>
      ),
    },
    {
      key: 'outcome',
      header: 'Status',
      render: (row: IssuanceTestRequestStatus) =>
        row.outcome === 'issued' ? (
          <StatusBadge label="Wystawiono" variant="success" />
        ) : row.outcome === 'failed' ? (
          <StatusBadge label="Błąd" variant="danger" />
        ) : (
          <StatusBadge label="Przetwarzanie" variant="info" pulse />
        ),
    },
    {
      key: 'durationSeconds',
      header: 'Czas (s)',
      render: (row: IssuanceTestRequestStatus) =>
        row.durationSeconds != null ? `${row.durationSeconds}s` : <span className="text-gray-400">—</span>,
    },
    {
      key: 'error',
      header: 'Błąd',
      render: (row: IssuanceTestRequestStatus) => (
        <span className="text-xs text-red-500 dark:text-red-300/80 max-w-xs truncate block" title={row.error ?? ''}>
          {row.error || '—'}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{detail.templateTitle}</p>
          <p className="text-xs text-gray-500">
            {detail.recipientDomain} · {formatDateTime(detail.startedAt)}
          </p>
        </div>
        {detail.status === 'running' ? (
          <StatusBadge label="Przetwarzanie" variant="info" pulse />
        ) : (
          <StatusBadge label="Zakończono" variant="success" />
        )}
      </div>

      {/* Plain-language summary */}
      <p className="text-sm text-gray-700 dark:text-gray-300">
        Zlecono <strong>{detail.requested}</strong> poświadczeń:{' '}
        <strong className="text-emerald-600 dark:text-emerald-400">{detail.issued}</strong> wystawiono poprawnie,{' '}
        <strong className={detail.failed > 0 ? 'text-red-600 dark:text-red-400' : ''}>{detail.failed}</strong> z błędem
        {detail.pending > 0 && (
          <>
            , <strong className="text-blue-600 dark:text-blue-400">{detail.pending}</strong> w toku
          </>
        )}
        .{' '}
        {avg != null && (
          <>
            Średni czas wystawienia pojedynczego poświadczenia: <strong>{avg}s</strong>.
          </>
        )}
      </p>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <MetricCard title="Zlecono" value={detail.requested} icon={<Rocket className="h-5 w-5 text-gray-500" />} />
        <MetricCard
          title="Wystawiono"
          value={detail.issued}
          variant="success"
          icon={<CheckCircle2 className="h-5 w-5 text-emerald-400" />}
        />
        <MetricCard
          title="Błędy"
          value={detail.failed}
          variant={detail.failed > 0 ? 'danger' : 'default'}
          icon={<XCircle className="h-5 w-5 text-red-400" />}
        />
        <MetricCard
          title="W toku"
          value={detail.pending}
          variant={detail.pending > 0 ? 'warning' : 'default'}
          icon={<Clock className="h-5 w-5 text-blue-400" />}
        />
        <MetricCard
          title="Śr. czas"
          value={avg != null ? `${avg}s` : '—'}
          subtitle="na poświadczenie"
          icon={<Timer className="h-5 w-5 text-gray-500" />}
        />
        <MetricCard
          title="Tempo"
          value={throughput != null ? `${throughput}/min` : '—'}
          subtitle={detail.totalElapsedSeconds != null ? `łącznie w ${detail.totalElapsedSeconds}s` : 'przepustowość'}
          icon={<Gauge className="h-5 w-5 text-gray-500" />}
        />
      </div>

      {avg != null && throughput != null && (
        <p className="text-xs text-gray-400 -mt-1">
          Śr. czas to czas jednego poświadczenia w pipeline; tempo to łączna przepustowość — platforma przetwarza
          poświadczenia równolegle, więc obie wartości mogą się różnić.
        </p>
      )}

      {detail.errors.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-lg p-3">
          <p className="text-xs font-semibold text-red-700 dark:text-red-300 mb-2">Błędy</p>
          <ul className="space-y-1">
            {detail.errors.map((e, i) => (
              <li key={i} className="text-xs text-red-600 dark:text-red-300/90 flex items-start gap-2">
                <span className="font-mono shrink-0">{e.count}×</span>
                <span>{e.error}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <DataTable columns={columns} data={detail.rows} compact emptyMessage="Brak zleceń" />
    </div>
  );
}
